<template>
  <div class="container-fluid">
    <div class="row starter-main mt-5 pt-5">
      <TableTitle
        title="Support Tickets List"
        pageTitle="Support Ticket"
        icon="fas fa-ticket-alt"
        :showAction="false"
      />
      
      <CommonTable2
        :tableObj="tableConfig"
        @edit="handleEdit"
        @view="handleView"
        @delete="handleDelete"
        @statusFilter="handleStatusFilter"
        @priorityFilter="handlePriorityFilter"
        @resetFilters="resetFilters"
        @search="(val) => { searchQuery = val; handleSearch(); }"
        permissionKey="support"
      />
    </div>
  </div>

  <!-- pagination -->
  <div class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4">
    <div class="d-flex align-items-center gap-2">
      <span class="text-muted">Rows per page:</span>
      <select
        class="form-select form-select-sm rows-select"
        v-model="tablePagination.per_page"
        @change="changeRows"
      >
        <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
          {{ n }}
        </option>
      </select>
    </div>
    <div class="pagination-mini">
      <button
        class="page-btn prev_btn"
        :disabled="!tablePagination.has_prev"
        @click="handlePageChange(tablePagination.current_page - 1)"
      >
        «
      </button>
      <button
        v-for="(p, index) in visiblePages"
        :key="index"
        class="page-btn"
        :class="{
          'page-active': p === tablePagination.current_page,
          'disabled-dots': p === '...',
        }"
        :disabled="p === '...'"
        @click="p !== '...' && typeof p === 'number' && handlePageChange(p) "
      >
        {{ p }}
      </button>
      <button
        class="page-btn next_btn"
        :disabled="!tablePagination.has_next"
        @click="handlePageChange(tablePagination.current_page + 1)"
      >
        »
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useCrud } from "@/composables/commen/useCrud";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import Swal from 'sweetalert2';
import apiService from "@/services/apiService";
import { toast } from "vue3-toastify";

// Manual debounce implementation
const debounce = (fn: Function, delay: number) => {
  let timeout: any;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
};

const props = defineProps({
  fixedStatus: {
    type: String,
    default: ""
  }
});

const API_RESOURCE = "/common/admin/support/tickets";
const router = useRouter();

// Use the generic CRUD composable
const crud = useCrud(API_RESOURCE);

// Filters State
const searchQuery = ref("");
const filterStatus = ref(props.fixedStatus || "");
const filterPriority = ref("");

watch(() => props.fixedStatus, (newVal) => {
  filterStatus.value = newVal || "";
  tableConfig.value.showStatusFilter = !newVal;
  currentPage.value = 1;
  fetchTickets();
});

// pagination local UI state
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const currentPage = ref(1);

// visiblePages computed
const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});

// Table configuration
const rows = ref<any[]>([]);
const tableConfig = ref({
  columns: [
    { name: "Name", field: "name", sortable: true }, 
    { name: "Subject", field: "subject", sortable: true },
    { name: "Category", field: "category", sortable: true },
    { name: "Priority", field: "priority", type: 'badge', sortable: true },
    { name: "Status", field: "status", type: 'badge', sortable: true },
    { name: "Created Date", field: "created_at", type: 'date', sortable: true },
  ],
  data: rows,
  actions: [true],
  loading: crud.isLoading,
  showStatusFilter: false,
  showPriorityFilter: true,
  showResetFilter: true,
  allStatusLables: ["open", "closed"],
  allPriorityLables: ["low", "medium", "high"],
  table_width: "100%",
});

watch(crud.data, (newVal) => {
  rows.value = newVal || [];
});

watch(crud.isLoading, (newVal) => {
  tableConfig.value.loading = newVal;
});

// Sync backend pagination into tablePagination
watch(
  () => crud.pagination.value,
  (newVal) => {
    if (!newVal) return;

    tablePagination.value.current_page = newVal.current_page || 1;
    tablePagination.value.total_pages = newVal.total_pages || 1;
    tablePagination.value.total = newVal.total || 0;
    tablePagination.value.per_page = tablePagination.value.per_page;
    tablePagination.value.has_prev = tablePagination.value.current_page > 1;
    tablePagination.value.has_next =
    tablePagination.value.current_page < tablePagination.value.total_pages;
  },
  { immediate: true, deep: true }
);

// === ACTIONS ===
const fetchTickets = async () => {
  const params: any = {
    page: currentPage.value,
    limit: tablePagination.value.per_page,
    status: props.fixedStatus || filterStatus.value || (props.fixedStatus ? undefined : "open"),
  };

  if (searchQuery.value) params.search = searchQuery.value;
  if (filterPriority.value) params.priority = filterPriority.value;

  await crud.fetchData(params);
};

const handleSearch = debounce(() => {
  currentPage.value = 1;
  fetchTickets();
}, 500);

const handleStatusFilter = (val: string) => {
  filterStatus.value = val.toLowerCase();
  currentPage.value = 1;
  fetchTickets();
};

const handlePriorityFilter = (val: string) => {
  filterPriority.value = val.toLowerCase();
  currentPage.value = 1;
  fetchTickets();
};

const resetFilters = () => {
  searchQuery.value = "";
  filterStatus.value = "";
  filterPriority.value = "";
  currentPage.value = 1;
  fetchTickets();
};

const changeRows = async () => {
  currentPage.value = 1;
  await fetchTickets();
};

const handlePageChange = async (page: number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (
    tablePagination.value.total_pages &&
    page > tablePagination.value.total_pages
  )
    return;

  currentPage.value = page;
  await fetchTickets();
};

const handleView = (row: any) => {
  const basePath = props.fixedStatus === 'closed' ? '/closed-tickets' : '/open-tickets';
  router.push(`${basePath}/${row._id}?mode=view`);
};

const handleEdit = (row: any) => {
  const basePath = props.fixedStatus === 'closed' ? '/closed-tickets' : '/open-tickets';
  router.push(`${basePath}/${row._id}`);
};

const handleDelete = async (data: any) => {
  const id = typeof data === 'string' ? data : data._id;
  try {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#21c1d6',
      cancelButtonColor: '#f8d7da',
      confirmButtonText: 'Yes, delete it!',
      customClass: {
        confirmButton: 'btn btn-primary',
        cancelButton: 'btn btn-outline-danger ms-2'
      },
      buttonsStyling: false
    });

    if (result.isConfirmed) {
      await apiService.delete(API_RESOURCE, id);
      toast.success("Ticket deleted successfully", { theme: "colored" });
      await fetchTickets();
    }
  } catch (err: any) {
    console.error("Failed to delete ticket:", err);
    toast.error(err.response?.data?.message || "Failed to delete ticket", { theme: "colored" });
  }
};

onMounted(async () => {
  await fetchTickets();
});
</script>

<style scoped>
.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}

.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}

.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  border-radius: 0px;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}
.page-btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}
.page-btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}
.page-btn:hover {
  background: #e9ecef;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-active {
  background: #21c1d6;
  color: #fff;
  padding: 7px 12px;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
  display: inline-block;
  border: 1px solid #21c1d6;
}
.page-active:hover {
  background: #21c1d6;
}
.prev_btn,
.next_btn {
  color: #21c1d6;
}
</style>
